import { registerApi as api } from '../../api';

const pickRegisterError = (error) => {
  const d = error.response?.data;
  if (typeof d?.message === 'object' && d.message?.error) return d.message.error;
  if (typeof d?.message === 'string') return d.message;
  if (typeof d?.error === 'string') return d.error;
  return error.response?.data?.error?.message || error.message;
};

const authHeaders = () => ({
  Authorization: `Bearer ${sessionStorage.getItem('token')}`,
});

const parseBlobError = async (error) => {
  const data = error.response?.data;
  if (data instanceof Blob) {
    try {
      const text = await data.text();
      const parsed = JSON.parse(text);
      if (typeof parsed?.error === 'string') return parsed.error;
    } catch {
      // ignore parse errors
    }
  }
  return pickRegisterError(error);
};

export const fetchComplianceDocumentSetup = async ({ category, document_id, document_name }) => {
  try {
    const response = await api.get('/get_compliance_document_setup', {
      headers: authHeaders(),
      params: { category, document_id, document_name },
    });
    return response.data;
  } catch (error) {
    throw pickRegisterError(error);
  }
};

export const saveComplianceDocumentSetupApi = async (payload) => {
  try {
    const response = await api.put('/save_compliance_document_setup', payload, {
      headers: authHeaders(),
    });
    return response.data;
  } catch (error) {
    throw pickRegisterError(error);
  }
};

export const fetchComplianceDocuments = async (params) => {
  try {
    const response = await api.get('/get_compliance_documents', {
      headers: authHeaders(),
      params,
    });
    return Array.isArray(response.data) ? response.data : [];
  } catch (error) {
    throw pickRegisterError(error);
  }
};

export const uploadComplianceDocumentApi = async ({
  file,
  client_id,
  unit_id,
  category,
  document_id,
  document_name,
  validity_type,
  year,
  month,
  period_start,
  from_date,
  expiry_date,
  to_date,
  valid_until,
  answers,
  fields_json,
}) => {
  try {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('client_id', client_id);
    formData.append('unit_id', unit_id);
    formData.append('category', category);
    if (document_id) formData.append('document_id', document_id);
    if (document_name) formData.append('document_name', document_name);
    if (validity_type) formData.append('validity_type', validity_type);
    if (year) formData.append('year', year);
    if (month) formData.append('month', String(parseInt(month, 10)));
    if (period_start) formData.append('period_start', period_start);
    if (from_date) formData.append('from_date', from_date);
    if (expiry_date) formData.append('expiry_date', expiry_date);
    if (to_date) formData.append('to_date', to_date);
    if (valid_until) formData.append('valid_until', valid_until);
    if (answers) formData.append('answers', JSON.stringify(answers));
    if (fields_json) formData.append('fields_json', JSON.stringify(fields_json));
    const response = await api.post('/upload_compliance_document', formData, {
      headers: {
        ...authHeaders(),
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  } catch (error) {
    throw pickRegisterError(error);
  }
};

export const updateComplianceDocumentApi = async (id, payload) => {
  try {
    const formData = new FormData();
    Object.entries(payload || {}).forEach(([key, value]) => {
      if (value === undefined || value === null || value === '') return;
      if (key === 'file') {
        formData.append('file', value);
        return;
      }
      if (key === 'answers' || key === 'fields_json') {
        formData.append(key, JSON.stringify(value));
        return;
      }
      formData.append(key, value);
    });
    const response = await api.put(`/update_compliance_document/${id}`, formData, {
      headers: {
        ...authHeaders(),
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  } catch (error) {
    throw pickRegisterError(error);
  }
};

export const deleteComplianceDocumentApi = async (id) => {
  try {
    const response = await api.delete(`/delete_compliance_document/${id}`, {
      headers: authHeaders(),
    });
    return response.data;
  } catch (error) {
    throw pickRegisterError(error);
  }
};

const parseDispositionFilename = (disposition) => {
  if (!disposition) return "";
  const star = String(disposition).match(/filename\*\s*=\s*UTF-8''([^;]+)/i);
  if (star?.[1]) {
    try {
      return decodeURIComponent(star[1].replace(/['"]/g, "").trim());
    } catch {
      return star[1].replace(/['"]/g, "").trim();
    }
  }
  const plain = String(disposition).match(/filename\s*=\s*("?)([^";]+)\1/i);
  return plain?.[2] ? plain[2].trim() : "";
};

export const downloadComplianceDocumentApi = async (id, fallbackName) => {
  try {
    const response = await api.get(`/download_compliance_document/${id}`, {
      responseType: 'blob',
      headers: authHeaders(),
    });
    const contentType = response.headers['content-type'] || '';
    if (contentType.includes('application/json')) {
      const text = await response.data.text();
      const parsed = JSON.parse(text);
      throw parsed?.error || 'Download failed';
    }
    const disposition = response.headers['content-disposition'];
    let filename = parseDispositionFilename(disposition) || fallbackName || 'compliance_document';
    if (!/\.[a-z0-9]+$/i.test(filename) && contentType.includes('pdf')) {
      filename = `${filename}.pdf`;
    }
    const named = new File(
      [response.data],
      filename,
      { type: contentType || 'application/octet-stream' }
    );
    const url = window.URL.createObjectURL(named);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    link.parentNode.removeChild(link);
    window.URL.revokeObjectURL(url);
    return { success: true, status: response.status };
  } catch (error) {
    throw await parseBlobError(error);
  }
};

export const viewComplianceDocumentBlob = async (id) => {
  try {
    const response = await api.get(`/view_compliance_document/${id}`, {
      responseType: 'blob',
      headers: authHeaders(),
    });
    const contentType = response.headers['content-type'] || '';
    if (contentType.includes('application/json')) {
      const text = await response.data.text();
      try {
        const parsed = JSON.parse(text);
        return { blob: null, error: parsed?.error || 'unavailable' };
      } catch {
        return { blob: null, error: 'unavailable' };
      }
    }
    return { blob: response.data, contentType, status: response.status };
  } catch (error) {
    return { blob: null, error: await parseBlobError(error) };
  }
};

export const complianceDocumentViewIframeUrl = (id) => {
  const token = sessionStorage.getItem('token') || localStorage.getItem('token') || '';
  const base = String(api.defaults.baseURL || '').replace(/\/$/, '');
  const params = new URLSearchParams();
  if (token) params.set('jwt', token);
  const query = params.toString();
  return `${base}/view_compliance_document/${id}${query ? `?${query}` : ''}`;
};

export const downloadComplianceDocumentsZipApi = async (ids, filename) => {
  try {
    const response = await api.post('/download_compliance_documents_zip', {
      ids,
      filename: filename || 'compliance_documents.zip',
    }, {
      responseType: 'blob',
      headers: authHeaders(),
    });
    const contentType = response.headers['content-type'] || '';
    if (contentType.includes('application/json')) {
      const text = await response.data.text();
      const parsed = JSON.parse(text);
      throw parsed?.error || 'Download failed';
    }
    const disposition = response.headers['content-disposition'];
    const name = parseDispositionFilename(disposition) || filename || 'compliance_documents.zip';
    const named = new File(
      [response.data],
      name,
      { type: contentType || 'application/zip' }
    );
    const url = window.URL.createObjectURL(named);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', name);
    document.body.appendChild(link);
    link.click();
    link.parentNode.removeChild(link);
    window.URL.revokeObjectURL(url);
    return { success: true, status: response.status };
  } catch (error) {
    throw await parseBlobError(error);
  }
};

export const viewComplianceDocumentApi = async (id) => {
  try {
    const response = await api.get(`/view_compliance_document/${id}`, {
      responseType: 'blob',
      headers: authHeaders(),
    });
    const contentType = response.headers['content-type'] || '';
    if (contentType.includes('application/json')) {
      const text = await response.data.text();
      const parsed = JSON.parse(text);
      throw parsed?.error || 'View failed';
    }
    return {
      success: true,
      blob: response.data,
      contentType,
      status: response.status,
    };
  } catch (error) {
    throw await parseBlobError(error);
  }
};

export const releaseComplianceDocumentApi = async (id) => {
  try {
    const response = await api.post(`/release_compliance_document/${id}`, {}, {
      headers: authHeaders(),
    });
    return response.data;
  } catch (error) {
    throw pickRegisterError(error);
  }
};

export const releaseComplianceDocumentsApi = async (ids) => {
  try {
    const response = await api.post('/release_compliance_documents', { ids }, {
      headers: authHeaders(),
    });
    return response.data;
  } catch (error) {
    throw pickRegisterError(error);
  }
};

export const fetchComplianceDocumentCatalog = async () => {
  try {
    const response = await api.get('/get_compliance_document_catalog', {
      headers: authHeaders(),
    });
    return response.data;
  } catch (error) {
    throw pickRegisterError(error);
  }
};

export const createComplianceDocumentCatalogApi = async (payload) => {
  try {
    const response = await api.post('/create_compliance_document_catalog', payload, {
      headers: authHeaders(),
    });
    return response.data;
  } catch (error) {
    throw pickRegisterError(error);
  }
};

export const deleteComplianceDocumentCatalogApi = async (itemId) => {
  try {
    const response = await api.delete(`/delete_compliance_document_catalog/${itemId}`, {
      headers: authHeaders(),
    });
    return response.data;
  } catch (error) {
    throw pickRegisterError(error);
  }
};
